import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export interface MikrotikNeighborInfo {
  name: string | null
  ip: string | null
  source: 'mndp' | 'dhcp' | 'bridge'
}

export interface MikrotikPort {
  port: string
  link: boolean
  speed: string | null
  rate_rx_bytes_s: number | null
  rate_tx_bytes_s: number | null
  sfp_temp_c: number | null
  sfp_rx_dbm: number | null
  sfp_tx_dbm: number | null
  sfp_vendor: string | null
  sfp_model: string | null
  sfp_temp_max_24h: number | null
  neighbor: MikrotikNeighborInfo | null
}

export interface MikrotikDevice {
  device: string
  ts: string
  age_s: number
  reachable: boolean
  cpu_pct: number | null
  mem_used_pct: number | null
  temp_board_c: number | null
  uptime_s: number | null
  version: string | null
  board_name: string | null
  ports: MikrotikPort[]
}

export interface MikrotikTempThresholds {
  warn_c: number
  crit_c: number
}

export interface MikrotikLatest {
  temp_thresholds: MikrotikTempThresholds
  devices: MikrotikDevice[]
}

export interface MikrotikSeries {
  range: string
  bucket_seconds: number
  device_points: { b: string; device: string; temp_board_c: number | null; cpu_pct: number | null }[]
  port_points: { b: string; device: string; port: string; sfp_temp_c: number | null }[]
}

export function useMikrotik() {
  return useQuery<MikrotikLatest>({
    queryKey: ['mikrotik'],
    queryFn: () => api<MikrotikLatest>('/api/metrics/mikrotik'),
    refetchInterval: 30_000,
  })
}

export function useMikrotikSeries(range = '24h') {
  return useQuery<MikrotikSeries>({
    queryKey: ['mikrotik-series', range],
    queryFn: () => api<MikrotikSeries>(`/api/metrics/mikrotik/series?range=${range}`),
    refetchInterval: 60_000,
  })
}

/** Tone for an SFP temperature vs the alert-rule thresholds. */
export function portTempTone(
  t: number | null,
  th: MikrotikTempThresholds,
): 'ok' | 'warn' | 'crit' | 'none' {
  if (t == null) return 'none'
  if (t >= th.crit_c) return 'crit'
  if (t >= th.warn_c) return 'warn'
  return 'ok'
}

/** "sfp-sfpplus2" → "SFP+2", "ether4" → "ether4" (display label). */
export function portLabel(port: string): string {
  const m = port.match(/^sfp-sfpplus(\d+)$/)
  return m ? `SFP+${m[1]}` : port
}

/** Pivot port_points (long) → wide rows keyed by bucket, one column per
 * device:port, plus board temp columns per device. */
export function pivotSeries(s: MikrotikSeries): {
  rows: Record<string, unknown>[]
  keys: string[]
} {
  const rows = new Map<string, Record<string, unknown>>()
  const keys = new Set<string>()
  for (const p of s.port_points) {
    const k = `${p.device} ${portLabel(p.port)}`
    keys.add(k)
    const row = rows.get(p.b) ?? { b: p.b }
    row[k] = p.sfp_temp_c
    rows.set(p.b, row)
  }
  for (const d of s.device_points) {
    if (d.temp_board_c == null) continue
    const k = `${d.device} carte`
    keys.add(k)
    const row = rows.get(d.b) ?? { b: d.b }
    row[k] = d.temp_board_c
    rows.set(d.b, row)
  }
  return {
    rows: [...rows.values()].sort((a, b) =>
      String(a.b).localeCompare(String(b.b)),
    ),
    keys: [...keys].sort(),
  }
}
